import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import {
  Button,
  Progress,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@learn-me/ui";
import {
  X,
  Sparkles,
  BookOpen,
  Clock,
  GraduationCap,
  Languages,
  CheckCircle,
  AlertCircle,
  FileText,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useAIGeneration } from "@/hooks/useAIGeneration";
import type {
  AIGenerationParams,
  AIGenerationJob,
  TempFile,
} from "@/features/admin/api/adminApi";
import { FileUploader } from "./FileUploader";

interface AIGenerationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type FormData = {
  topic: string;
  duration: "SHORT" | "MEDIUM" | "LONG";
  level: "BEGINNER" | "INTERMEDIATE" | "ADVANCED";
  language: "es" | "en";
  includeQuizzes: boolean;
  additionalNotes: string;
};

const DURATION_OPTIONS = [
  {
    value: "SHORT",
    label: "Corto",
    description: "5 capítulos (~2-3 horas)",
    icon: "⚡",
  },
  {
    value: "MEDIUM",
    label: "Medio",
    description: "10 capítulos (~5-8 horas)",
    icon: "📚",
  },
  {
    value: "LONG",
    label: "Extenso",
    description: "15-20 capítulos (~10+ horas)",
    icon: "🎓",
  },
] as const;

const LEVEL_OPTIONS = [
  {
    value: "BEGINNER",
    label: "Principiante",
    description: "Sin conocimientos previos",
  },
  {
    value: "INTERMEDIATE",
    label: "Intermedio",
    description: "Conocimientos básicos",
  },
  {
    value: "ADVANCED",
    label: "Avanzado",
    description: "Profesionales/expertos",
  },
] as const;

export function AIGenerationModal({ isOpen, onClose }: AIGenerationModalProps) {
  const navigate = useNavigate();
  const [step, setStep] = useState<"form" | "generating" | "preview">("form");
  const [uploadedFiles, setUploadedFiles] = useState<TempFile[]>([]);
  const [showDocsSection, setShowDocsSection] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<FormData>({
    defaultValues: {
      topic: "",
      duration: "MEDIUM",
      level: "BEGINNER",
      language: "es",
      includeQuizzes: true,
      additionalNotes: "",
    },
  });

  const {
    isGenerating,
    job,
    error,
    startGeneration,
    confirmCourse,
    cancelGeneration,
    reset,
  } = useAIGeneration({
    onComplete: () => setStep("preview"),
    onError: () => setStep("form"),
  });

  const handleClose = () => {
    if (isGenerating) {
      if (confirm("¿Estás seguro de cancelar la generación en progreso?")) {
        cancelGeneration();
        reset();
        setStep("form");
        setUploadedFiles([]);
        setShowDocsSection(false);
        onClose();
      }
    } else {
      reset();
      setStep("form");
      setUploadedFiles([]);
      setShowDocsSection(false);
      onClose();
    }
  };

  const onSubmit = async (data: FormData) => {
    setStep("generating");
    const params: AIGenerationParams = {
      ...data,
      tempFiles: uploadedFiles.length > 0 ? uploadedFiles : undefined,
    };
    await startGeneration(params);
  };

  const handleConfirm = async () => {
    const course = await confirmCourse();
    if (course) {
      handleClose();
      navigate(`/admin/courses/${course.id}/edit`);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white rounded-xl shadow-2xl m-4">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between p-6 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-t-xl">
          <div className="flex items-center gap-3">
            <Sparkles className="w-6 h-6" />
            <div>
              <h2 className="text-xl font-bold">Generar Curso con IA</h2>
              <p className="text-sm text-purple-200">
                {step === "form" && "Configura los parámetros de tu curso"}
                {step === "generating" && "Generando contenido..."}
                {step === "preview" && "¡Curso generado exitosamente!"}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {step === "form" && (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Topic */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <BookOpen className="w-4 h-4" />
                  Tema del curso *
                </label>
                <input
                  {...register("topic", {
                    required: "El tema es requerido",
                    minLength: { value: 5, message: "Mínimo 5 caracteres" },
                    maxLength: { value: 200, message: "Máximo 200 caracteres" },
                  })}
                  type="text"
                  placeholder="Ej: Introducción a Python para principiantes"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                {errors.topic && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.topic.message}
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  {watch("topic")?.length || 0}/200 caracteres
                </p>
              </div>

              {/* Duration */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <Clock className="w-4 h-4" />
                  Duración del curso
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {DURATION_OPTIONS.map((option) => (
                    <label
                      key={option.value}
                      className={`relative flex flex-col items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        watch("duration") === option.value
                          ? "border-purple-500 bg-purple-50"
                          : "border-gray-200 hover:border-purple-300"
                      }`}
                    >
                      <input
                        {...register("duration")}
                        type="radio"
                        value={option.value}
                        className="sr-only"
                      />
                      <span className="text-2xl mb-1">{option.icon}</span>
                      <span className="font-medium text-gray-900">
                        {option.label}
                      </span>
                      <span className="text-xs text-gray-500 text-center">
                        {option.description}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Level */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <GraduationCap className="w-4 h-4" />
                  Nivel de dificultad
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {LEVEL_OPTIONS.map((option) => (
                    <label
                      key={option.value}
                      className={`relative flex flex-col items-center p-3 border-2 rounded-lg cursor-pointer transition-all ${
                        watch("level") === option.value
                          ? "border-purple-500 bg-purple-50"
                          : "border-gray-200 hover:border-purple-300"
                      }`}
                    >
                      <input
                        {...register("level")}
                        type="radio"
                        value={option.value}
                        className="sr-only"
                      />
                      <span className="font-medium text-gray-900">
                        {option.label}
                      </span>
                      <span className="text-xs text-gray-500 text-center">
                        {option.description}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Language & Quizzes */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                    <Languages className="w-4 h-4" />
                    Idioma
                  </label>
                  <select
                    {...register("language")}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="es">Español</option>
                    <option value="en">English</option>
                  </select>
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                    ✅ Opciones
                  </label>
                  <label className="flex items-center gap-3 p-3 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      {...register("includeQuizzes")}
                      type="checkbox"
                      className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
                    />
                    <span className="text-sm text-gray-700">
                      Incluir quizzes por capítulo
                    </span>
                  </label>
                </div>
              </div>

              {/* Additional Notes */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Notas adicionales (opcional)
                </label>
                <textarea
                  {...register("additionalNotes", {
                    maxLength: {
                      value: 2000,
                      message: "Máximo 2000 caracteres",
                    },
                  })}
                  placeholder="Ej: Enfocado en desarrollo web, incluir ejemplos prácticos..."
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                />
                {errors.additionalNotes && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.additionalNotes.message}
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  {watch("additionalNotes")?.length || 0}/2000 caracteres
                </p>
              </div>

              {/* Reference Documents (Expandable) */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => setShowDocsSection(!showDocsSection)}
                  className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-purple-600" />
                    <span className="font-medium text-gray-700">
                      Documentos de referencia
                    </span>
                    {uploadedFiles.length > 0 && (
                      <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                        {uploadedFiles.length} archivo
                        {uploadedFiles.length !== 1 ? "s" : ""}
                      </span>
                    )}
                  </div>
                  {showDocsSection ? (
                    <ChevronUp className="w-4 h-4 text-gray-500" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-gray-500" />
                  )}
                </button>

                {showDocsSection && (
                  <div className="p-4 border-t border-gray-200">
                    <p className="text-sm text-gray-600 mb-4">
                      Sube PDFs o imágenes que sirvan como referencia para
                      generar el contenido del curso. La IA analizará estos
                      documentos para crear contenido más preciso y relevante.
                    </p>
                    <FileUploader
                      files={uploadedFiles}
                      onFilesChange={setUploadedFiles}
                      maxFiles={5}
                    />
                  </div>
                )}
              </div>

              {/* Error message */}
              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-red-800">
                      Error en la generación
                    </p>
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                </div>
              )}

              {/* Submit */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={handleClose}>
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Generar Curso
                </Button>
              </div>
            </form>
          )}

          {step === "generating" && job && (
            <GeneratingView job={job} onCancel={handleClose} />
          )}

          {step === "preview" && job && (
            <PreviewView
              job={job}
              onConfirm={handleConfirm}
              onCancel={handleClose}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// Generating View Component
function GeneratingView({
  job,
  onCancel,
}: {
  job: AIGenerationJob;
  onCancel: () => void;
}) {
  return (
    <div className="text-center py-8">
      <div className="mb-6">
        <div className="relative w-24 h-24 mx-auto">
          <div className="absolute inset-0 rounded-full border-4 border-purple-200"></div>
          <div
            className="absolute inset-0 rounded-full border-4 border-purple-600 border-t-transparent animate-spin"
            style={{ animationDuration: "1.5s" }}
          ></div>
          <Sparkles className="absolute inset-0 m-auto w-10 h-10 text-purple-600" />
        </div>
      </div>

      <h3 className="text-xl font-semibold text-gray-900 mb-2">
        Generando tu curso...
      </h3>
      <p className="text-gray-600 mb-6">
        {job.currentStep || "Iniciando generación..."}
      </p>

      {/* Progress */}
      <div className="max-w-md mx-auto mb-6">
        <div className="flex justify-between text-sm text-gray-600 mb-2">
          <span>Progreso</span>
          <span>{job.progress}%</span>
        </div>
        <Progress value={job.progress} className="h-3" />
        <p className="text-xs text-gray-500 mt-2">
          Paso {job.completedSteps} de {job.totalSteps}
        </p>
      </div>

      {/* Info cards */}
      <div className="grid grid-cols-3 gap-4 max-w-md mx-auto mb-8">
        <div className="p-3 bg-purple-50 rounded-lg">
          <p className="text-xs text-purple-600 font-medium">Tema</p>
          <p className="text-sm text-gray-800 truncate">{job.topic}</p>
        </div>
        <div className="p-3 bg-purple-50 rounded-lg">
          <p className="text-xs text-purple-600 font-medium">Duración</p>
          <p className="text-sm text-gray-800">{job.duration}</p>
        </div>
        <div className="p-3 bg-purple-50 rounded-lg">
          <p className="text-xs text-purple-600 font-medium">Nivel</p>
          <p className="text-sm text-gray-800">{job.level}</p>
        </div>
      </div>

      <Button variant="outline" onClick={onCancel}>
        Cancelar generación
      </Button>
    </div>
  );
}

// Preview View Component
function PreviewView({
  job,
  onConfirm,
  onCancel,
}: {
  job: AIGenerationJob;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const rawOutput = job.rawOutput as {
    title: string;
    description: string;
    chapters: Array<{ title: string; position: number; quiz?: unknown }>;
    gamification?: { badges?: unknown[]; activities?: unknown[] };
  } | null;

  if (!rawOutput) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="w-16 h-16 mx-auto text-red-500 mb-4" />
        <p className="text-gray-600">No se pudo cargar la vista previa</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Success header */}
      <div className="text-center">
        <CheckCircle className="w-16 h-16 mx-auto text-green-500 mb-4" />
        <h3 className="text-xl font-semibold text-gray-900">
          ¡Curso generado exitosamente!
        </h3>
        <p className="text-gray-600">
          Revisa el contenido y confirma para crear el curso
        </p>
      </div>

      {/* Course preview */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{rawOutput.title}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-600 mb-4">{rawOutput.description}</p>

          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-700">
              📚 {rawOutput.chapters?.length || 0} capítulos generados:
            </p>
            <div className="max-h-48 overflow-y-auto space-y-1">
              {rawOutput.chapters?.map((chapter, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-2 text-sm text-gray-600 py-1"
                >
                  <span className="w-6 h-6 flex items-center justify-center bg-purple-100 text-purple-600 rounded-full text-xs font-medium">
                    {chapter.position}
                  </span>
                  <span>{chapter.title}</span>
                  {!!chapter.quiz && (
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                      Quiz
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Gamification preview */}
          {rawOutput.gamification && (
            <div className="mt-4 pt-4 border-t">
              <p className="text-sm font-medium text-gray-700 mb-2">
                🎮 Elementos de gamificación:
              </p>
              <div className="flex gap-4 text-xs text-gray-600">
                {rawOutput.gamification.badges && (
                  <span>🏆 {rawOutput.gamification.badges.length} badges</span>
                )}
                {rawOutput.gamification.activities && (
                  <span>
                    🎯 {rawOutput.gamification.activities.length} actividades
                  </span>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button variant="outline" onClick={onCancel}>
          Descartar
        </Button>
        <Button onClick={onConfirm} className="bg-green-600 hover:bg-green-700">
          <CheckCircle className="w-4 h-4 mr-2" />
          Crear Curso
        </Button>
      </div>
    </div>
  );
}
